/**
 * Labstep
 *
 * @module components/List/Tree
 * @desc List with hierarchical tree structure
 */

import { EntitySearchContainer as EntitySearchCursor } from 'labstep-web/components/Entity/Search/Cursor';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { ParamsContext } from 'labstep-web/contexts/params';
import { TreeItem } from 'labstep-web/core/Tree/Item';
import { ParamsHOC } from 'labstep-web/hoc/Params';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import type { TreeEntity } from 'labstep-web/models';
import type React from 'react';
import type { TreeContainerProps, TreeProps } from './types';
import {
  getSetActiveTreeEntity,
  isActiveTreeEntity,
  sortByName,
} from './utils';

export const Tree: React.FC<TreeProps> = (props) => {
  const {
    entities,
    level = 0,
    unsorted,
    entityName,
    childKeyName,
    activeGroup,
    createSubAction,
    icon,
    iconInactive,
    actionMenu,
    getCornerIcon,
    levelMargin = 10,
    maxLevels = 4,
    flatten,
    mapping,
    postFilter,
    folderType,
    ...rest
  } = props;
  const mappedEntities = mapping ? mapping(entities) : entities;
  return (
    <ParamsHOC historyAction="replace">
      <ParamsContext.Consumer>
        {({
          setParams,
          searchParams,
          addPostFilter,
        }): React.ReactNode => {
          const setActiveTreeEntity = getSetActiveTreeEntity(
            setParams,
            addPostFilter,
            postFilter,
          );
          return (
            <>
              {unsorted && level === 0 && !flatten && (
                <TreeItem
                  icon={unsorted.icon}
                  text={unsorted.text}
                  active={!!searchParams[unsorted.param]}
                  onClick={() => {
                    setParams({
                      [`${entityName}_id`]: undefined,
                      [unsorted.param]: true,
                    });
                  }}
                />
              )}
              {mappedEntities.map((entity) => {
                const isActive = isActiveTreeEntity(
                  searchParams,
                  entity,
                  postFilter,
                );
                const marginLeft =
                  level < maxLevels && level !== 0 && !flatten
                    ? levelMargin
                    : 0;
                const cornerIconProps = getCornerIcon?.(entity) || {};
                return (
                  <div key={entity.id}>
                    <div
                      style={{
                        marginLeft: `${marginLeft}px`,
                      }}
                    >
                      <TreeItem
                        icon={
                          !isActive && iconInactive
                            ? iconInactive
                            : icon
                        }
                        cornerIcon={cornerIconProps.cornerIcon}
                        cornerIconPosition={
                          cornerIconProps.cornerIconPosition
                        }
                        text={entity.name}
                        active={isActive}
                        onClick={(): void =>
                          setActiveTreeEntity(
                            entityName,
                            entity,
                            unsorted,
                          )
                        }
                        actionMenu={actionMenu?.(entity, {
                          onSuccess: () => {
                            if (isActive) {
                              setActiveTreeEntity(
                                entityName,
                                undefined,
                                unsorted,
                              );
                            }
                          },
                        })}
                        {...rest}
                      >
                        {!flatten && (
                          <ReadOnMountHOC
                            type="cursor"
                            entityName={entityName}
                            params={{
                              group_id: activeGroup?.id,
                              [`${childKeyName}_id`]: entity.guid,
                              type: folderType,
                              sort: 'name',
                            }}
                            pagination={{ infinite: false }}
                            children={({
                              entities: childEntities,
                            }) =>
                              childEntities.length > 0 ? (
                                <Tree
                                  {...props}
                                  entities={childEntities}
                                  level={level + 1}
                                />
                              ) : (
                                <div
                                  style={{
                                    marginLeft: `${
                                      marginLeft + levelMargin
                                    }px`,
                                    marginBottom: `${levelMargin}px`,
                                  }}
                                >
                                  {createSubAction &&
                                    createSubAction(entity)}
                                </div>
                              )
                            }
                          />
                        )}
                      </TreeItem>
                    </div>
                  </div>
                );
              })}
            </>
          );
        }}
      </ParamsContext.Consumer>
    </ParamsHOC>
  );
};

Tree.defaultProps = {
  level: 0,
  levelMargin: 10,
  maxLevels: 4,
};

export const TreeContainer: React.FC<TreeContainerProps> = ({
  activeGroup,
  entityName,
  childKeyName,
  createSubAction,
  icon,
  iconInactive,
  actionMenu,
  getCornerIcon,
  unsorted,
  params,
  action,
  searchBarProps,
  postFilter,
  folderType,
}) => {
  if (!activeGroup) {
    return null;
  }

  return (
    <EntitySearchCursor
      entityName={entityName}
      params={(searchParams) => {
        const commonParams = {
          group_id: activeGroup?.id,
          ...params,
        };
        if (
          typeof searchParams.search_query === 'string' &&
          searchParams.search_query?.length > 0
        ) {
          return commonParams;
        }
        return {
          ...commonParams,
          sort: 'name',
          [`has_${childKeyName}`]: false,
        };
      }}
      emptyStateAction={action}
      searchBarProps={searchBarProps}
      showEmptyStateOnSearchOnly
    >
      {({ entities, searchParams }) => {
        const searchActive = !!searchParams.search_query;
        return (
          <>
            <Tree
              activeGroup={activeGroup}
              entities={entities as TreeEntity[]}
              entityName={entityName}
              childKeyName={childKeyName}
              createSubAction={createSubAction}
              icon={icon}
              iconInactive={iconInactive}
              actionMenu={actionMenu}
              getCornerIcon={getCornerIcon}
              unsorted={unsorted}
              flatten={searchActive}
              mapping={!searchActive ? sortByName : undefined}
              postFilter={postFilter}
              folderType={folderType}
            />
            {!searchActive && action}
          </>
        );
      }}
    </EntitySearchCursor>
  );
};

export default withActiveGroup(TreeContainer);
